// src/api.js - HTTP client with cold-start retry
import axios from 'axios';
import logger from './utils/logger';

const baseURL = process.env.REACT_APP_API_URL || 'https://financegpt-backend-clean.onrender.com';

// Cold start retry configuration
// Render free tier can take up to 150s to wake up
const INITIAL_TIMEOUT = 30000;      // 30s for first attempt
const COLD_START_TIMEOUT = 180000;  // 180s for retry (server waking up)

// Listeners for server wake-up state (UI can subscribe to show status)
let _serverWakingListeners = [];

export const onServerWaking = (callback) => {
  _serverWakingListeners.push(callback);
  return () => {
    _serverWakingListeners = _serverWakingListeners.filter(cb => cb !== callback);
  };
};

const notifyServerWaking = (isWaking) => {
  _serverWakingListeners.forEach(cb => cb(isWaking));
};

// Session-expiry handling: on a 401 the stored JWT is invalid/expired. Clear auth
// state and send the user to a clean login with a notice. Guarded so parallel
// 401s (e.g. dashboard poll + a click) don't each trigger a redirect.
let _authFailureHandled = false;
const handleAuthFailure = () => {
  if (_authFailureHandled) return;
  _authFailureHandled = true;
  try {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user');
  } catch (_) { /* ignore storage errors */ }
  if (!window.location.search.includes('session=expired')) {
    window.location.href = '/?session=expired';
  }
};

// Check if an error is a timeout/network error (server probably sleeping)
const isColdStartError = (error) => {
  if (error.code === 'ECONNABORTED') return true;          // axios timeout
  if (error.message?.includes('timeout')) return true;      // generic timeout
  if (error.message?.includes('Network Error')) return true; // server unreachable
  if (!error.response && error.isAxiosError) return true;   // no response at all
  return false;
};

const api = {
  // Core request method with cold-start retry
  async request(config) {
    const timeout = config.timeout || INITIAL_TIMEOUT;
    const token = localStorage.getItem('jwt_token');
    const skipRetry = config._isRetry || config.skipRetry;

    const makeRequest = (requestTimeout) => this._axiosRequest(config, token, requestTimeout);

    try {
      return await makeRequest(timeout);
    } catch (error) {
      // Session expired / invalid token → clean re-login. Skip during the login
      // exchange itself, which surfaces its own error to the user.
      if (error?.response?.status === 401 && !config.url?.includes('/auth/exchange-token')) {
        handleAuthFailure();
        throw error;
      }

      // If it's a cold-start error and we haven't retried yet, retry with longer timeout
      if (!skipRetry && isColdStartError(error)) {
        logger.warn('Server may be waking up from cold start. Retrying...');
        notifyServerWaking(true);

        try {
          const result = await makeRequest(COLD_START_TIMEOUT);
          notifyServerWaking(false);
          return result;
        } catch (retryError) {
          notifyServerWaking(false);
          throw retryError;
        }
      }

      throw error;
    }
  },

  // Axios HTTP
  async _axiosRequest(config, token, timeout) {
    return axios({
      baseURL,
      timeout,
      ...config,
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...config.headers
      }
    });
  },

  // Convenience methods
  async get(url, config = {}) {
    return this.request({ method: 'GET', url, ...config });
  },

  async post(url, data, config = {}) {
    return this.request({ method: 'POST', url, data, ...config });
  },

  async put(url, data, config = {}) {
    return this.request({ method: 'PUT', url, data, ...config });
  },

  async delete(url, config = {}) {
    return this.request({ method: 'DELETE', url, ...config });
  }
};

// Helper functions for token management
// POST that reads a Server-Sent Events response. Calls onEvent for every parsed
// event and resolves with the final "done" event. Same auth and 401 handling as
// api.request; a cold start is covered by the long timeout rather than a retry,
// because a streamed request cannot be replayed once tokens have arrived.
export const streamPost = async (url, body, { onEvent, timeoutMs = COLD_START_TIMEOUT } = {}) => {
  const token = localStorage.getItem('jwt_token');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(`${baseURL}${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    throw e;
  }

  if (res.status === 401 && !url.includes('/auth/exchange-token')) {
    clearTimeout(timer);
    handleAuthFailure();
    const err = new Error('Session expired');
    err.response = { status: 401, data: {} };
    throw err;
  }
  if (!res.ok || !res.body) {
    clearTimeout(timer);
    let detail = null;
    try { detail = (await res.json()).detail; } catch (e) { /* no JSON body */ }
    const err = new Error((detail && detail.message) || `Request failed (${res.status})`);
    err.response = { status: res.status, data: { detail } };
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done = null;
  try {
    for (;;) {
      const { value, done: finished } = await reader.read();
      if (finished) break;
      buffer += decoder.decode(value, { stream: true });
      let idx = buffer.indexOf('\n\n');
      while (idx >= 0) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const line = raw.split('\n').find((l) => l.startsWith('data: '));
        if (line) {
          let evt = null;
          try { evt = JSON.parse(line.slice(6)); } catch (e) { evt = null; }
          if (evt) {
            if (evt.type === 'done') done = evt;
            if (onEvent) onEvent(evt);
          }
        }
        idx = buffer.indexOf('\n\n');
      }
    }
  } finally {
    clearTimeout(timer);
  }
  if (!done) throw new Error('The answer was cut off before it finished.');
  return done;
};

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('jwt_token', token);
  } else {
    localStorage.removeItem('jwt_token');
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('jwt_token');
};

export const clearAuthToken = () => {
  localStorage.removeItem('jwt_token');
};

export const isAuthenticated = () => {
  const token = getAuthToken();
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;

    if (payload.exp && payload.exp < now) {
      clearAuthToken();
      return false;
    }

    return true;
  } catch (error) {
    clearAuthToken();
    return false;
  }
};

// Chart request function for modal
export const requestChart = async (symbol, period = '6mo', chartType = 'line') => {
  try {
    // Include user_id for fallback auth (when JWT token exchange failed during login)
    const savedUser = localStorage.getItem('user');
    const userId = savedUser ? JSON.parse(savedUser)?.uid : undefined;

    const response = await api.post('/v1/charts/generate', {
      symbol: symbol.toUpperCase(),
      period,
      chart_type: chartType,
      ...(userId && { user_id: userId })
    });
    return response.data;
  } catch (error) {
    console.error('Chart request failed:', error);
    throw error;
  }
};

// Lab and digest
export const runWhatIf = (body) => api.post('/v1/portfolio/whatif', body).then((r) => r.data);
export const fetchScenarios = (body) => api.post('/v1/portfolio/scenarios', body).then((r) => r.data);
export const sendDigestTest = () => api.post('/v1/digest/send-test').then((r) => r.data);

// Shared by the chart modal on every page: turns API errors into a message the modal can show.
export const chartRequestHandler = async ({ symbol, period, chart_type }) => {
  try {
    const result = await requestChart(symbol, period, chart_type);
    return { chart_config: result };
  } catch (error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 429) return { error: detail?.message || 'Chart limit reached for today.' };
    if (status === 400) return { error: detail?.message || `No price history for ${symbol}.` };
    return { error: detail?.message || 'Could not draw that chart.' };
  }
};

export default api;
